// Legacy-hash redirect shim. Old flat hashes (bookmarks + the Iron Dome
// overview tiles, which navigate by setting window.location.hash to a
// backend-supplied slug) → canonical nested paths. Each becomes a root-level
// redirect route in router.tsx; the bare-hash normalizer there prepends the
// leading slash so `#slug` resolves. Kept as a plain table so the smoke test
// can assert the whole mapping at once.
//
// 2026-09-18 nav regrouping: Uploads / Database / Tasks / Automations / SFTP
// Logs moved under /admin, Master Recon + Position Recon under /trading-desk,
// and Digit became Accounts → Rebalance Calculator. Every old top-level path
// (and its sub-paths) is listed here so no bookmark or tile breaks.
export const LEGACY_REDIRECTS: Record<string, string> = {
  // Uploads → Admin
  upload: "/admin/uploads/excel",
  "trade-uploads": "/admin/uploads/trades",
  uploads: "/admin/uploads/trades",
  "uploads/trades": "/admin/uploads/trades",
  "uploads/excel": "/admin/uploads/excel",
  // Other former top-level tabs → Admin
  database: "/admin/database",
  "scheduled-tasks": "/admin/tasks",
  automations: "/admin/automations",
  "sftp-logs": "/admin/sftp-logs",
  // Reconciliation → Trading Desk
  "master-recon": "/trading-desk/master-recon/recon",
  "master-recon/recon": "/trading-desk/master-recon/recon",
  "master-recon/mappings": "/trading-desk/master-recon/mappings",
  "position-recon": "/trading-desk/position-recon",
  // Digit → Accounts › Rebalance Calculator
  digit: "/accounts/rebalance",
  "digit/rebalance": "/accounts/rebalance",
  "digit/models": "/accounts/rebalance/models",
  "digit/restricted": "/accounts/rebalance/restricted",
  // Fee Calc → Finance (2026-09-23). The path-form key outranks the
  // `accounts/*` splat that would otherwise swallow it: two static segments
  // score higher than one plus a splat.
  "accounts/fees": "/finance/fees",
  // Fund Facts
  generate: "/fund-facts/generate",
  "fund-facts-v2": "/fund-facts/v2",
  "fund-facts-all": "/fund-facts/performance",
  "fund-facts-performance": "/fund-facts/performance-charts",
  "fund-facts-models": "/fund-facts/models",
  "fund-facts-etf-metadata": "/fund-facts/etf-metadata",
  "etf-metadata": "/fund-facts/etf-metadata",
  "fund-facts-fund-admin": "/admin/fund-admin",
  "fund-facts/fund-admin": "/admin/fund-admin",
  // Trading Desk → Iron Dome
  health: "/trading-desk/iron-dome",
  "iron-dome": "/trading-desk/iron-dome",
  "iron-dome-positions": "/trading-desk/iron-dome/positions",
  "iron-dome-options": "/trading-desk/iron-dome/options-expiry",
  "iron-dome-hedged-series": "/trading-desk/iron-dome/hedged-series",
  "iron-dome-corp-actions": "/trading-desk/iron-dome/corp-actions",
  "iron-dome-option-restrikes": "/trading-desk/iron-dome/option-restrikes",
  "iron-dome-avg-price": "/trading-desk/iron-dome/avg-price",
  "iron-dome-pipelines": "/trading-desk/iron-dome/pipelines",
  // Trading Desk → Cash Monitor
  "cash-monitor": "/trading-desk/cash",
  // FX Hedging is a Trade Builder sub-tab (was a Cash Monitor one). The
  // path-form key outranks Cash Monitor's `cash/*` splat: three static
  // segments score higher than two plus a splat.
  "fx-hedging": "/trading-desk/trade/fx-hedging",
  "trading-desk/cash/fx-hedging": "/trading-desk/trade/fx-hedging",
  // Partner Orders left Cash Monitor for its own page beside Trade Builder (2026-10-29).
  "trading-desk/cash/partner-orders": "/trading-desk/partner-trades",
  "bloomberg-pending": "/trading-desk/cash/bloomberg-pending",
  "iron-dome-bloomberg": "/trading-desk/cash/bloomberg-pending",
  // Trading Desk → other pills
  "trade-building": "/trading-desk/trade",
  "bloomberg-query": "/trading-desk/bloomberg",
  ibkr: "/trading-desk/ibkr",
  "money-movements": "/trading-desk/money-movements",
  // The Iron Dome margin tile deep-links with this hash. Margin Reports is an
  // Iron Dome sub-tab now; its own sub-tabs (totals/records/…) hang off it.
  "trade-processing": "/trading-desk/iron-dome/trade-processing",
  "margin-reports": "/trading-desk/iron-dome/margin-reports",
  "trading-desk/margin-reports": "/trading-desk/iron-dome/margin-reports",
  "trading-desk/margin-reports/*": "/trading-desk/iron-dome/margin-reports",
  // Compliance and Shadow NAV left Iron Dome (2026-09-18): Compliance is an
  // Accounts page, Shadow NAV a Fund Facts › Performance page.
  "trading-desk/iron-dome/compliance": "/accounts/compliance",
  // The Iron Dome "Error Accounts" tile.
  "error-accounts": "/accounts/error-accounts",
  "trading-desk/iron-dome/shadow-nav": "/fund-facts/shadow-nav",
  // The Iron Dome "Fundserv Holdings" tile deep-links with this hash; the Recon
  // sub-tab is the one it is pointing at, and FundservHoldings reads its own
  // sub-tab from the splat.
  "fundserv-holdings": "/trading-desk/fundserv-holdings",
  // The Iron Dome "Blotter vs Capstock" tile. Lands on the Trade Blotters
  // sub-tab, whose Corrections inner tab is component state rather than a
  // route — so this is as deep as a hash can currently go.
  "blotter-corrections": "/trading-desk/cash/trade-blotter",
};
